import { useCallback } from "react";
import * as Popover from "@radix-ui/react-popover";
import { User, Trophy, LogOut, Settings } from "lucide-react";
import { LanguageSwitcher } from "./LanguageSwitcher";
import { SquadSwitcher } from "./SquadSwitcher";
import {
  trackHeaderClick,
  trackNavigation,
  trackLogout,
} from "../lib/analytics";
import { useLanguage } from "../contexts/LanguageContext";
import { useAuth } from "../contexts/AuthContext";
import { useModal } from "../contexts/ModalContext";
import { UserMetrics } from "./UserMetrics";
import type { Screen } from "../types";

interface HeaderProps {
  onNavigate?: (screen: Exclude<Screen, "predictor">) => void;
  showNav?: boolean;
}

export function Header({ onNavigate, showNav = false }: HeaderProps) {
  const { t } = useLanguage();
  const { user, profile, signOut, isAdmin } = useAuth();
  const { openLoginModal } = useModal();

  const handleLogout = useCallback(async () => {
    trackHeaderClick("logout");
    trackLogout();
    await signOut();
  }, [signOut]);

  return (
    <>
      <header className="bg-white/80 backdrop-blur-md shadow-sm sticky top-0 z-50 border-b border-slate-200">
        <div className="max-w-md mx-auto px-4 py-4 flex items-center justify-between gap-1.5">
          <div
            className="flex items-center gap-1.5 cursor-pointer transition-smooth hover:opacity-80 flex-shrink min-w-0"
            onClick={() => {
              trackHeaderClick("logo");
              trackNavigation("landing");
              onNavigate?.("landing");
            }}
          >
            <div className="flex-shrink-0">
              <img
                src="/logo.png"
                alt={t("app.name")}
                className="w-9 h-9 rounded-xl object-contain"
              />
            </div>
            <span className="font-semibold text-primary text-sm leading-tight">
              {t("app.name")}
            </span>
          </div>

          <div className="flex items-center gap-0.5">
            {showNav && (
              <button
                onClick={() => {
                  trackHeaderClick("leaderboard");
                  trackNavigation("leaderboard");
                  onNavigate?.("leaderboard");
                }}
                className="h-9 w-9 flex items-center justify-center hover:bg-slate-100 rounded-xl transition-smooth relative"
              >
                <Trophy className="w-5 h-5 text-slate-600" />
                {/* Notification circle */}
                <span className="absolute top-1 right-1 w-2 h-2 bg-red-500 rounded-full border-2 border-white"></span>
              </button>
            )}
            {user && <SquadSwitcher />}
            <LanguageSwitcher />
            {isAdmin && (
              <button
                onClick={() => {
                  trackHeaderClick("admin");
                  trackNavigation("admin");
                  onNavigate?.("admin");
                }}
                className="h-9 w-9 flex items-center justify-center hover:bg-slate-100 rounded-xl transition-smooth"
                title={t("admin.panel")}
              >
                <Settings className="w-5 h-5 text-slate-600" />
              </button>
            )}

            {/* User Button / Menu */}
            {!user || !profile ? (
              <button
                onClick={() => {
                  trackHeaderClick("login");
                  openLoginModal();
                }}
                className="h-9 w-9 flex items-center justify-center hover:bg-slate-100 rounded-xl transition-smooth"
                aria-label="Login"
              >
                <User className="w-5 h-5 text-slate-600" />
              </button>
            ) : (
              <Popover.Root>
                <Popover.Trigger asChild>
                  <button
                    className="h-9 w-9 flex items-center justify-center hover:bg-slate-100 rounded-xl transition-smooth"
                    aria-label="User menu"
                  >
                    <div className="w-7 h-7 bg-gradient-to-br from-primary to-primary-dark rounded-full flex items-center justify-center">
                      <span className="text-white text-xs font-semibold">
                        {profile.name.charAt(0).toUpperCase()}
                      </span>
                    </div>
                  </button>
                </Popover.Trigger>

                <Popover.Portal>
                  <Popover.Content
                    className="bg-white border border-slate-200 shadow-xl rounded-2xl overflow-hidden z-50 p-1 min-w-[240px] animate-in fade-in-0 zoom-in-95 data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=closed]:zoom-out-95"
                    sideOffset={8}
                    align="end"
                    collisionPadding={10}
                  >
                    {/* User Info */}
                    <div className="px-4 py-3">
                      <div className="font-semibold text-slate-900 text-sm">
                        {profile.name}
                      </div>
                      <div className="text-xs text-slate-500 truncate mt-0.5">
                        {profile.email}
                      </div>
                    </div>

                    {/* User Metrics */}
                    <div className="mb-1">
                      <div className="bg-slate-100 rounded-xl py-3 flex items-center justify-center">
                        <UserMetrics variant="inline" showLabel={true} />
                      </div>
                    </div>

                    {/* Logout Button */}
                    <Popover.Close asChild>
                      <button
                        onClick={handleLogout}
                        className="w-full h-[44px] px-4 flex items-center gap-3 hover:bg-red-50 transition-smooth rounded-[15px] text-left"
                      >
                        <LogOut className="w-5 h-5 text-red-600 flex-shrink-0" />
                        <span className="text-sm font-semibold text-red-700 flex-1">
                          {t("login.logout")}
                        </span>
                      </button>
                    </Popover.Close>
                  </Popover.Content>
                </Popover.Portal>
              </Popover.Root>
            )}
          </div>
        </div>
      </header>
    </>
  );
}
